import { axiosInstance } from '@/providers/axiosInstance';
import type { AdminTokenResponse } from '@/types';

const CLIENT_FLUFFY_BASE =
  import.meta.env.VITE_CLIENT_FLUFFY_URL ?? 'http://localhost:3000';

/** Lifetimes the "Copy link" dropdown offers. The server caps a token at 7 days. */
export const SHARE_LINK_DURATIONS = [
  { key: '1h', label: '1 hour', minutes: 60 },
  { key: '1d', label: '1 day', minutes: 1440 },
  { key: '7d', label: '7 days', minutes: 10080 },
] as const;

async function fetchAdminToken(ttlMinutes?: number): Promise<string> {
  const response = await axiosInstance.post<AdminTokenResponse>(
    '/api/admin/token',
    ttlMinutes === undefined ? undefined : { ttlMinutes }
  );
  return response.data.token;
}

function buildAdminLink(path: string, token: string): string {
  return `${CLIENT_FLUFFY_BASE}${path}?admin_token=${encodeURIComponent(token)}`;
}

/**
 * Opens a client-fluffy page (preview, confirmation, generating) in a new tab,
 * authenticated with a freshly-minted admin token.
 *
 * The blank tab is opened synchronously inside the click handler so the popup
 * blocker permits it, then navigated once the token resolves. We must NOT pass
 * `noopener`/`noreferrer` to `window.open` here: per the HTML spec those
 * features make it return `null`, which would strand the tab on about:blank
 * forever (the navigation in `.then` is guarded by `if (win)`). Instead we keep
 * the handle and sever the opener link manually after navigating.
 */
export function openWithAdminToken(path: string): void {
  const win = window.open('about:blank', '_blank');
  fetchAdminToken()
    .then((token) => {
      if (!win) return;
      win.opener = null;
      win.location.href = buildAdminLink(path, token);
    })
    .catch(() => {
      if (win) win.close();
    });
}

/**
 * Mints ONE admin token of the given lifetime and copies the client-fluffy link
 * for `path` to the clipboard. Resolves `'copied'`, or `{ manualUrl }` when the
 * clipboard is unavailable or refuses, so the caller can show the link instead.
 * A failed token fetch rejects.
 *
 * Safari only allows a clipboard write inside the click's user activation, and
 * the token arrives later. So `navigator.clipboard.write` is called
 * SYNCHRONOUSLY (no `await` before it) with a promise-valued `ClipboardItem`
 * that resolves once the token does. The caller must not await anything before
 * calling this either. The fallback reuses the same token, never mints a second.
 */
export function copyAdminLink(
  path: string,
  ttlMinutes: number
): Promise<'copied' | { manualUrl: string }> {
  const tokenPromise = fetchAdminToken(ttlMinutes);

  if (typeof ClipboardItem === 'undefined' || !navigator.clipboard?.write) {
    return tokenPromise.then((token) => ({
      manualUrl: buildAdminLink(path, token),
    }));
  }

  const blobPromise = tokenPromise.then(
    (token) => new Blob([buildAdminLink(path, token)], { type: 'text/plain' })
  );
  // The browser consumes `blobPromise`; this keeps a throw before it does from
  // leaving a rejected token fetch unhandled.
  blobPromise.catch(() => undefined);

  let written: Promise<void>;
  try {
    written = navigator.clipboard.write([
      new ClipboardItem({ 'text/plain': blobPromise }),
    ]);
  } catch {
    written = Promise.reject(new Error('clipboard write unavailable'));
  }

  return written.then(
    () => 'copied' as const,
    // Awaiting `tokenPromise` rethrows a token-fetch failure to the caller.
    async () => ({ manualUrl: buildAdminLink(path, await tokenPromise) })
  );
}
